// Hilfen für das Kosten-Dashboard (S23): Monate, Einheiten, Budget-Texte. Rein, ohne Netzwerk.
import type { CostBudget, CostConsumption } from "./api/types";
import { formatCount, formatEuro } from "./format";

const MONTH_NAMES = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

/** „2026-10“ → „Oktober 2026“ */
export function monthLabel(month: string): string {
  const [year, number] = month.split("-");
  return `${MONTH_NAMES[Number(number) - 1] ?? month} ${year}`;
}

/** Vorheriger oder nächster Monat im Format JJJJ-MM. */
export function shiftMonth(month: string, delta: number): string {
  const [year, number] = month.split("-").map(Number);
  const index = (year ?? 0) * 12 + ((number ?? 1) - 1) + delta;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
}

/** Aktueller Monat im Format JJJJ-MM (Zeitzone des Browsers). */
export function currentMonth(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** Betrag in der Einheit der Quelle: Euro mit Symbol, sonst Zahl mit Einheit („2.600 Units“). */
export function formatAmount(amount: number, unit: string): string {
  return unit === "€" ? formatEuro(amount) : `${formatCount(Math.round(amount))} ${unit}`;
}

/** „2.600 Units · 4 Credits · 21.000 Tokens“ oder ein Strich ohne Verbrauch. */
export function consumptionText(list: readonly CostConsumption[]): string {
  return list.length === 0 ? "–" : list.map((c) => formatAmount(c.amount, c.unit)).join(" · ");
}

export const BUDGET_STATE_TEXT: Record<CostBudget["state"], string> = {
  none: "Kein Monatsbudget gesetzt",
  ok: "Im Rahmen",
  warn: "Über 80 % des Budgets verbraucht",
  over: "Monatsbudget überschritten",
};

export const BUDGET_STATE_CLASS: Record<CostBudget["state"], string> = {
  none: "bg-neutral-bg text-muted",
  ok: "bg-success-bg text-success",
  warn: "bg-warning-bg text-warning",
  over: "bg-danger-bg text-danger",
};

/** Anteil in Prozent für die Anzeige, auf 100 % begrenzt für die Balkenbreite. */
export function percent(share: number): number {
  return Math.round(share * 100);
}

export function barWidth(share: number): number {
  return Math.max(0, Math.min(100, percent(share)));
}

/** Warum ein Job auf Freigabe wartet (aus dem Job). */
export function approvalText(job: {
  approval_reason?: "job_limit" | "monthly_budget" | null;
  estimated_cost_eur: number | null;
  cost_limit_eur: number;
  monthly_budget_eur?: number;
  month_spent_eur?: number;
}): string {
  const estimate = formatEuro(job.estimated_cost_eur ?? 0);
  if (job.approval_reason === "monthly_budget") {
    const left = Math.max(0, (job.monthly_budget_eur ?? 0) - (job.month_spent_eur ?? 0));
    return `Geschätzte Kosten von ${estimate} übersteigen den Rest des Monatsbudgets (${formatEuro(left)} von ${formatEuro(job.monthly_budget_eur ?? 0)}). Der Job läuft erst nach einer Freigabe weiter.`;
  }
  return `Geschätzte Kosten von ${estimate} liegen über dem Limit von ${formatEuro(job.cost_limit_eur)} pro Job. Der Job läuft erst nach einer Freigabe weiter.`;
}
